/**
 * TransitionPicker - Compact transition selector between slides
 */
import React, { useState } from 'react';
import {
    Box,
    Typography,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Slider,
    Tooltip,
} from '@mui/material';
import {
    BlurOn as FadeIcon,
    ArrowBack as SlideLeftIcon,
    ArrowForward as SlideRightIcon,
    ArrowUpward as SlideUpIcon,
    ArrowDownward as SlideDownIcon,
    ContentCut as CutIcon,
} from '@mui/icons-material';

const TRANSITIONS = [
    { key: 'fade', label: 'Fade', icon: <FadeIcon fontSize="small" /> },
    { key: 'slide_left', label: 'Slide Links', icon: <SlideLeftIcon fontSize="small" /> },
    { key: 'slide_right', label: 'Slide Rechts', icon: <SlideRightIcon fontSize="small" /> },
    { key: 'slide_up', label: 'Slide Omhoog', icon: <SlideUpIcon fontSize="small" /> },
    { key: 'slide_down', label: 'Slide Omlaag', icon: <SlideDownIcon fontSize="small" /> },
    { key: 'hard_cut', label: 'Hard Cut', icon: <CutIcon fontSize="small" /> },
];

export default function TransitionPicker({ slide, onUpdate }) {
    const [anchorEl, setAnchorEl] = useState(null);

    const currentTransition = TRANSITIONS.find(t => t.key === slide.transition_type) || TRANSITIONS[0];
    const isHardCut = slide.transition_type === 'hard_cut';

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                alignSelf: 'center',
                flexShrink: 0,
            }}
        >
            {/* Transition button */}
            <Tooltip title={`Overgang: ${currentTransition.label}`}>
                <Box
                    onClick={(e) => setAnchorEl(e.currentTarget)}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.3,
                        px: 0.75,
                        py: 0.25,
                        borderRadius: 1,
                        border: '1px solid #e5e7eb',
                        bgcolor: '#f9fafb',
                        cursor: 'pointer',
                        '&:hover': { borderColor: '#7c3aed', bgcolor: '#faf5ff' },
                    }}
                >
                    {React.cloneElement(currentTransition.icon, { sx: { fontSize: 13, color: '#9ca3af' } })}
                    <Typography variant="caption" sx={{ fontSize: '0.6rem', color: '#9ca3af', lineHeight: 1 }}>
                        {isHardCut ? 'Cut' : `${(slide.transition_duration || 0.5).toFixed(1)}s`}
                    </Typography>
                </Box>
            </Tooltip>

            {/* Dropdown menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                PaperProps={{ sx: { minWidth: 200 } }}
            >
                {TRANSITIONS.map(t => (
                    <MenuItem
                        key={t.key}
                        selected={slide.transition_type === t.key}
                        onClick={() => {
                            onUpdate(slide.id, {
                                transition_type: t.key,
                                transition_duration: t.key === 'hard_cut' ? 0 : (slide.transition_duration || 0.5),
                            });
                            setAnchorEl(null);
                        }}
                    >
                        <ListItemIcon>{t.icon}</ListItemIcon>
                        <ListItemText>{t.label}</ListItemText>
                    </MenuItem>
                ))}

                {/* Duration slider (hidden for hard_cut) */}
                {!isHardCut && (
                    <Box sx={{ px: 2, pt: 1, pb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                            Duur: {(slide.transition_duration || 0.5).toFixed(1)}s
                        </Typography>
                        <Slider
                            value={slide.transition_duration || 0.5}
                            onChange={(e, val) => onUpdate(slide.id, { transition_duration: val })}
                            min={0.1}
                            max={2.0}
                            step={0.1}
                            size="small"
                        />
                    </Box>
                )}
            </Menu>
        </Box>
    );
}
